import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ia, inRn } from 'lib/helpers/utility';
import { useInvoice } from 'lib/hooks/queries/public/useInvoice';
import { PracticeBrandingContextProvider } from 'lib/context/PracticeBrandingContext/PracticeBrandingContextProvider';

import NotFound from 'components/NotFound';
import Button from 'components/shared/Button/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import { getProceduresTotal } from 'components/practice/BeyondBilling/ElectronicInvoicing/components/Invoice/components/InvoiceFooter/InvoiceSummary/lib/helpers';

import SuccessPayment from './SuccessPayment';
import { isPatientRedacted } from './lib/helpers';
import { useVerifiedPatient } from './lib/useVerifiedPatient';
import InvoiceWithTokenContent from './InvoiceWithTokenContent';
import InvoiceWithTokenSkeleton from './InvoiceWithTokenSkeleton';
import VerifyPatient from './components/VerifyPatient/VerifyPatient';
import SecurePayment from './components/SecurePayment/SecurePayment';

const InvoiceWithToken = ({ invoice }) => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [transactionId, setTransactionId] = useState(null);

  const { data, isLoading } = useInvoice({
    params: { navigate, token },
    dependencies: [token],
    options: { enabled: !invoice && !!token }
  });

  const {
    invoice: externalInvoice,
    isVerifyOpen,
    isVerifying,
    openVerify,
    closeVerify,
    verify
  } = useVerifiedPatient({ token, invoice: invoice || data?.invoice });

  const type = externalInvoice?.type;
  const isKiosk = type === 'kiosk';
  const isAEOB = externalInvoice?.aeob;

  const practice = externalInvoice?.practice;
  const invoices = externalInvoice?.internal_invoices || [];
  const paid = externalInvoice?.paid;

  let patientInvoice = {
    discount: externalInvoice?.discount_amount_cents,
    tax: externalInvoice?.tax_percentage,
    tax_amount_cents: externalInvoice?.tax_amount_cents,
    surcharge_amount: inRn(externalInvoice?.surcharge_amount)
  };

  if (ia(invoices)) {
    const procedureTotalData = getProceduresTotal({ invoices, show: true, isAEOB });
    patientInvoice = { ...patientInvoice, ...procedureTotalData };
  }

  let { surcharge_amount = 0, total = 0 } = patientInvoice || {};

  if (isKiosk) total = externalInvoice?.total_amount_cents;

  const appliedSurcharge = !isKiosk && !paid ? surcharge_amount : 0;
  const amountDue = total + appliedSurcharge;
  // `undefined` while the invoice loads keeps the cached theme; `null` means no branding saved.
  const branding = externalInvoice ? (practice?.display_settings?.branding ?? null) : undefined;

  const onSuccess = (payload) => {
    const transactionId =
      typeof payload === 'string' || Number.isInteger(payload)
        ? payload
        : payload?.transactionId || payload?.receipt?.transaction?.id;
    setTransactionId(transactionId);
  };

  const renderPage = () => {
    if (!data?.invoice && !isLoading)
      return (
        <div className="h-dvh w-dvw overflow-hidden p-10 sm:p-4 xs:p-0">
          <NotFound />
        </div>
      );

    if (isLoading && !invoice) return <InvoiceWithTokenSkeleton />;

    if (transactionId) {
      return <SuccessPayment transactionId={transactionId} isKiosk={isKiosk} token={token} />;
    }

    return (
      <div className="h-dvh w-dvw overflow-hidden bg-gradient-to-b from-primary-50 to-primary-200 p-10 sm:p-4 xs:p-0">
        <div className="relative flex h-full flex-col rounded-xl bg-white shadow-[0px_0px_8px_rgba(65,70,81,0.1)] xs:rounded-none">
          <InvoiceWithTokenContent
            invoice={externalInvoice}
            invoices={invoices}
            amountDue={amountDue}
            surcharge={appliedSurcharge}
            billedToAction={
              isPatientRedacted(externalInvoice) && (
                <Button
                  text="Show info"
                  variant="secondary"
                  size="small"
                  className="mt-1"
                  onClick={openVerify}
                />
              )
            }
          />
          {amountDue > 0 && (
            <SecurePayment
              amountDue={amountDue}
              surchargeAmount={appliedSurcharge}
              isKioskInvoice={isKiosk}
              practice={practice}
              patient={externalInvoice?.patient}
              invoices={invoices}
              token={token}
              onSuccess={onSuccess}
              invoice={externalInvoice}
              presetGratuity={data?.presetGratuity}
            />
          )}
        </div>
        <VerifyPatient
          isOpen={isVerifyOpen}
          handleClose={closeVerify}
          onVerify={verify}
          isLoading={isVerifying}
        />
      </div>
    );
  };

  return (
    <PracticeBrandingContextProvider branding={branding}>
      {renderPage()}
    </PracticeBrandingContextProvider>
  );
};

export default withErrorBoundary(InvoiceWithToken);
